/**
 * useProbeCall — one GPT-Live call over WebRTC, with Amoreg's governance
 * tools answering on the far side of our own server.
 *
 * The shape, in order:
 *
 *   1. `getUserMedia` → `RTCPeerConnection` with the `oai-events` data
 *      channel → SDP offer, sent once ICE gathering completes or after
 *      `ICE_WAIT_MS`, whichever is first (`iceSettled`).
 *   2. `POST /api/amoreg/probe/session { sdp, voice }` — our server starts
 *      the session with OpenAI (instructions, the examiner's file, the
 *      backend and its tools) and hands back only the SDP answer. The
 *      browser never holds a key or the configuration.
 *   3. `session.started` → the call is live and listening at once. If the
 *      person speaks first, the examiner follows where they steer; if they
 *      stay quiet for a beat, it opens the interview itself
 *      (`decideOpening`). From then on GPT-Live runs the conversation: it
 *      listens while it speaks, murmurs while the person talks, and yields
 *      when interrupted — there is no turn-taking to manage here.
 *   4. When the backend asks for a tool (`response.event` wrapping a
 *      `function_call`), the call is forwarded to `/api/amoreg/probe/tool`
 *      at once; when that backend response settles, every output goes back
 *      as `response.item.create` and one `response.create` continues it.
 *
 * Every event is decoded against the contracts (`decodeLiveEvent`) and runs
 * through `reduceProbeEvent`; this hook only owns the wires.
 *
 * An `AnalyserNode` on the remote audio feeds the activity ref the orb reads
 * per frame: the orb is the examiner, so it moves with the examiner's voice
 * — never with the microphone, which would make it pulse to the room.
 *
 * @module components/regulation/probe/useProbeCall
 */
import {
  ProbeSessionGrant,
  ProbeToolResult,
  type ProbeJurisdiction,
  type ProbeMode,
  type ProbeRecord,
  type ProbeVoice,
} from "@amoreg/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../../../environments/primary/target";
import { saveProbeInterview, type ProbeInterviewSummary } from "../../../state/probeHistory";
import {
  parseToolArguments,
  recordsOf,
  type ProbeToolArguments,
} from "../../../state/probeRecords";
import {
  decodeLiveEvent,
  functionCallOf,
  initialProbeState,
  reduceProbeEvent,
  settledDelegationOf,
  webSourcesOf,
  type ProbeActivity,
  type ProbeConversationState,
  type ProbeEvent,
  type ProbeFunctionCall,
  type ProbeSource,
} from "../../../state/probeSession";
import { toastManager } from "../../ui/toast";
import { isRestrictedProvidersRefusal, RESTRICTED_PROVIDERS_COPY } from "./probeRefusal";

/** How long `End` waits for GPT-Live to confirm the close before hanging up anyway. */
const CLOSE_GRACE_MS = 2_500;
/**
 * How long the offer waits for ICE gathering before going out with the
 * candidates it has. OpenAI's end is ICE-lite with public addresses: the
 * browser dials it, so our candidates are a courtesy, not a requirement —
 * the Realtime-era Probe sent its offer with none. Waiting for "complete"
 * instead stalled Start for 10 s or more on machines with VPN or Tailscale
 * interfaces, whose gathering never finishes quickly.
 */
export const ICE_WAIT_MS = 1_500;
/** How long the microphone prompt may take before the page says it is waiting on it. */
const MICROPHONE_PROMPT_NOTICE_MS = 800;
/**
 * From OpenAI's answer to `session.started`. Past this, the voice connection
 * is not coming up — usually a network that blocks WebRTC — and the page
 * says so instead of showing "Connecting…" forever.
 */
export const CONNECT_TIMEOUT_MS = 15_000;
/** A typed answer rides an instruction, which GPT-Live caps at 500 tokens. */
const MAX_TYPED_CHARS = 1_600;
/**
 * How long a live call waits in silence before the examiner opens. GPT-Live
 * takes a couple of seconds to compose a greeting, so the call is live —
 * and listening — before it: someone who knows what they want can say it
 * straight away, as they would to ChatGPT, and steer from the first word.
 */
export const OPENING_GRACE_MS = 1_200;
/**
 * More speech than this on the microphone means the person is talking,
 * and the examiner never recites its opening over them — GPT-Live hears
 * them and answers. Less (a cough, a chair) only restarts the quiet beat.
 */
export const STEERING_SPEECH_MS = 400;
/** Microphone RMS above which the person is taken to be speaking. */
const LOCAL_SPEECH_LEVEL = 0.06;

export type OpeningDecision = "wait" | "open" | "steered";

/**
 * Whether the examiner should open now. Pure, so the timing can be pinned.
 * The person's transcript trails their voice by a second or more — longer
 * than the quiet beat — so it is the microphone that says they are talking.
 */
export const decideOpening = (input: {
  readonly sinceStartMs: number;
  readonly sinceLocalSpeechMs: number;
  /** How long the microphone has heard speech since the session started. */
  readonly localSpeechMs: number;
  readonly personTranscribed: boolean;
}): OpeningDecision => {
  if (input.personTranscribed || input.localSpeechMs >= STEERING_SPEECH_MS) return "steered";
  return input.sinceStartMs >= OPENING_GRACE_MS && input.sinceLocalSpeechMs >= OPENING_GRACE_MS
    ? "open"
    : "wait";
};

/**
 * The opener must not turn the system's lightweight setup into a checklist
 * or override a brief the person already gave while the call connected.
 */
export const openingInstructions = (jurisdictions: ReadonlyArray<string>): string => {
  const scope =
    jurisdictions.length === 0
      ? "No jurisdiction is recorded. Ask about the topic first; on a later turn ask which jurisdiction applies only when the next question or rule depends on it."
      : `Recorded jurisdictions (reference only; do not recite the list): ${jurisdictions.join(", ")}. ${jurisdictions.length > 1 ? "Ask which one applies only when needed and not already specified." : "Use this scope unless the person clarifies otherwise."} Do not invent a regulator.`;
  return `Open in English with a brief introduction as Probe and explain that this is a practice session, once. Follow the system's session setup: ask one short question about what they want to practise if no topic is known. Use any role, jurisdiction, topic or mode they already supplied; do not ask whether they are ready or run a setup checklist. ${scope} If the person has already supplied a brief, follow their brief without restarting setup. Pause and listen.`;
};

/** A typed answer, handed to the examiner as the person's words, not as orders. */
export const typedAnswerInstructions = (text: string): string =>
  `The person typed this instead of saying it. Treat it exactly as their spoken answer and respond to it now. Their words, verbatim: """${text.slice(0, MAX_TYPED_CHARS)}"""`;

/** Why a call could not start — the page words each one. */
export type ProbeStartFailure =
  | "unconfigured"
  | "no-identity"
  | "unauthorized"
  | "forbidden"
  | "restricted"
  | "microphone"
  | "unsupported"
  | "busy"
  | "blocked"
  | "network";

const failureFromStatus = (status: number): ProbeStartFailure => {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "unconfigured";
  // The replica gateway's answer when the server did not respond in time.
  if (status === 503 || status === 504) return "busy";
  return "network";
};

export const describeStartFailure = (failure: ProbeStartFailure): string => {
  switch (failure) {
    case "unconfigured":
      return "Probe isn't configured on this server yet — it needs an OpenAI key and the domain API.";
    case "no-identity":
      return "Sign in with your Amoreg account to use Probe.";
    case "unauthorized":
      return "Your session has expired. Sign in again.";
    case "forbidden":
      return "You are not a member of this organization.";
    case "restricted":
      return RESTRICTED_PROVIDERS_COPY;
    case "microphone":
      return "Probe needs the microphone. Allow access and try again.";
    case "unsupported":
      return "This browser can't make voice calls. Try a current Chrome, Edge, Safari or Firefox.";
    case "busy":
      return "The examiner took too long to get ready. Try again in a moment.";
    case "blocked":
      return "The voice connection didn't come up. A VPN or firewall may be blocking it — try again, or from another network.";
    case "network":
      return "The call couldn't be set up. Check the connection and try again.";
  }
};

const decodeGrant = Schema.decodeUnknownOption(ProbeSessionGrant);
const decodeToolResult = Schema.decodeUnknownOption(ProbeToolResult);
const OpenAiMessage = Schema.Struct({ message: Schema.String });

/** Exchange the offer for OpenAI's answer, through our server. */
const openSession = async (
  organizationId: string,
  sdp: string,
  voice: ProbeVoice,
  mode: ProbeMode,
): Promise<ProbeSessionGrant | { readonly failure: string }> => {
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/api/amoreg/probe/session", { organizationId }),
    {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ sdp, voice, mode }),
    },
  ).catch(() => null);
  if (response === null) return { failure: describeStartFailure("network") };
  const text = await response.text().catch(() => "");
  if (!response.ok) {
    if (text.includes("no-workos-identity"))
      return { failure: describeStartFailure("no-identity") };
    if (text.includes("unconfigured")) return { failure: describeStartFailure("unconfigured") };
    if (isRestrictedProvidersRefusal(response.status, text)) {
      return { failure: describeStartFailure("restricted") };
    }
    // OpenAI's own words when it refused ("You have no credits…") beat a generic line.
    const vendor = response.status === 502 ? parseMessage(text) : null;
    return { failure: vendor ?? describeStartFailure(failureFromStatus(response.status)) };
  }
  return Option.getOrElse(decodeGrant(parseJson(text)), () => ({
    failure: describeStartFailure("network"),
  }));
};

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
};

const parseMessage = (text: string): string | null =>
  Option.getOrNull(
    Schema.decodeUnknownOption(OpenAiMessage)(parseJson(text)).pipe(
      Option.map((body) => `OpenAI refused the call: ${body.message}`),
    ),
  );

export interface ProbeToolOutcome {
  readonly ok: boolean;
  readonly reason?: string;
  readonly output: string;
  readonly sources: ReadonlyArray<ProbeSource>;
  readonly records: ReadonlyArray<ProbeRecord>;
}

const unreachable = (reason: string): ProbeToolOutcome => ({
  ok: false,
  reason,
  output: JSON.stringify({ error: reason }),
  sources: [],
  records: [],
});

/**
 * What a tool route's answer means for the call: `output` is the string the
 * backend model reads back; `sources` are the pages a web search found, for
 * the transcript. `body` is the route's JSON, or null when it never answered.
 */
export const toolOutcomeOf = (
  name: string,
  args: ProbeToolArguments,
  body: unknown,
): ProbeToolOutcome => {
  const result = decodeToolResult(body);
  if (Option.isNone(result)) return unreachable("The records could not be reached.");
  return "error" in result.value
    ? unreachable(result.value.error)
    : {
        ok: true,
        output: JSON.stringify(result.value.output ?? null),
        sources: webSourcesOf(name, result.value.output),
        records: recordsOf(name, args, result.value.output),
      };
};

/** Run one tool on our server. */
const runTool = async (
  organizationId: string,
  sessionId: string | null,
  call: ProbeFunctionCall,
  args: ProbeToolArguments,
): Promise<ProbeToolOutcome> => {
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/api/amoreg/probe/tool", { organizationId }),
    {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        name: call.name,
        arguments: call.arguments,
        ...(sessionId === null ? {} : { sessionId }),
      }),
    },
  ).catch(() => null);
  const body = response === null ? null : await response.json().catch(() => null);
  return toolOutcomeOf(call.name, args, body);
};

/** Resolve when ICE gathering completes or `ICE_WAIT_MS` passes — never fails. */
export const iceSettled = (peer: RTCPeerConnection): Promise<void> =>
  new Promise((resolve) => {
    if (peer.iceGatheringState === "complete") return resolve();
    const done = () => {
      clearTimeout(timeout);
      peer.removeEventListener("icegatheringstatechange", onChange);
      resolve();
    };
    const timeout = setTimeout(done, ICE_WAIT_MS);
    function onChange() {
      if (peer.iceGatheringState === "complete") done();
    }
    peer.addEventListener("icegatheringstatechange", onChange);
  });

/** RMS level of an analyser's current frame, roughly 0..1 for speech. */
const readLevel = (analyser: AnalyserNode, buffer: Uint8Array<ArrayBuffer>): number => {
  analyser.getByteTimeDomainData(buffer);
  let sum = 0;
  for (let index = 0; index < buffer.length; index += 1) {
    const centered = (buffer[index]! - 128) / 128;
    sum += centered * centered;
  }
  // Speech RMS sits around 0.05–0.3; scale so a normal voice reaches ~0.8.
  return Math.min(1, Math.sqrt(sum / buffer.length) * 3.2);
};

/**
 * Speaking, with hysteresis: on above a clear level, off only after a short
 * quiet — so the status line does not flicker between syllables.
 */
const SPEAKING_ON = 0.08;
const SPEAKING_OFF = 0.03;
const SPEAKING_HOLD_MS = 350;

interface CallResources {
  readonly peer: RTCPeerConnection;
  readonly channel: RTCDataChannel;
  readonly stream: MediaStream;
  readonly audio: HTMLAudioElement;
  readonly context: AudioContext;
  raf: number;
  closeTimer: ReturnType<typeof setTimeout> | null;
  /** Polls `decideOpening` from `session.started` until the opening is settled. */
  openingPoll: ReturnType<typeof setInterval> | null;
  /** Fails the call if `session.started` has not arrived `CONNECT_TIMEOUT_MS` after the answer. */
  connectTimer: ReturnType<typeof setTimeout> | null;
}

export interface ProbeCall {
  readonly state: ProbeConversationState;
  /** The call is up: the session started and has not closed. */
  readonly active: boolean;
  readonly muted: boolean;
  readonly model: string | null;
  /** Whether the examiner is audibly speaking right now. */
  readonly activity: ProbeActivity;
  /** 0..1, written per animation frame — hand to the orb, never render on it. */
  readonly activityRef: React.RefObject<number>;
  readonly start: () => Promise<void>;
  /** Hang up gracefully: GPT-Live finishes pending work, then the line drops. */
  readonly end: () => void;
  readonly toggleMute: () => void;
  /**
   * Answer in text. Live: the words go to the examiner as the person's
   * answer. Not live yet: starts the call and delivers them once it is.
   */
  readonly ask: (text: string) => void;
  /** End this interview and begin a fresh one — new session, empty transcript. */
  readonly restart: () => void;
}

export function useProbeCall(
  organizationId: string | null,
  voice: ProbeVoice,
  /** Guided feedback after every answer, or a real interview with a debrief at the end. */
  mode: ProbeMode = "guided",
  /** Told once the server confirms a finished interview was saved. */
  onSaved?: (organizationId: string, interview: ProbeInterviewSummary) => void,
): ProbeCall {
  const [state, dispatch] = useReducer(reduceProbeEvent, initialProbeState);
  /**
   * The same state, computed synchronously by the same pure reducer. A call
   * can end in the handler that delivered its last words (`session.closed`
   * right after a transcript delta), before React has committed them; the
   * save reads this, so the last line is never lost.
   */
  const latest = useRef(initialProbeState);
  const apply = useCallback((event: ProbeEvent) => {
    latest.current = reduceProbeEvent(latest.current, event);
    dispatch(event);
  }, []);
  const [muted, setMuted] = useState(false);
  const [model, setModel] = useState<string | null>(null);
  const [activity, setActivity] = useState<ProbeActivity>("listening");
  const activityRef = useRef(0);
  const resources = useRef<CallResources | null>(null);
  /**
   * Which start attempt is current. Before the microphone is granted an
   * attempt owns no resources for teardown to close, so teardown (and every
   * newer attempt) moves this on instead, and a stale attempt's notice and
   * continuation see they are no longer current and stand down.
   */
  const attemptRef = useRef(0);
  const orgRef = useRef(organizationId);
  orgRef.current = organizationId;
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  /** Sends on the live channel once the session has started; null otherwise. */
  const sendRef = useRef<((event: Record<string, unknown>) => void) | null>(null);
  /** An answer typed before the session started, delivered when it does. */
  const pendingAsk = useRef<string | null>(null);
  /**
   * First-line id + length of the last save, so end → unmount saves a call
   * once. Cleared when a call starts: a new conversation can look the same.
   */
  const savedSignature = useRef<string | null>(null);

  /** Keep what was said. */
  const persist = useCallback(
    (pageClosing: boolean) => {
      const organization = orgRef.current;
      const call = latest.current;
      const { transcript } = call;
      if (organization === null || transcript.length < 2) return;
      const signature = `${transcript[0]!.id}:${transcript.length}`;
      if (savedSignature.current === signature) return;
      savedSignature.current = signature;
      const attempt = attemptRef.current;
      void Promise.resolve(saveProbeInterview(organization, call, pageClosing)).then((outcome) => {
        if (outcome.status === "skipped") return;
        if (attemptRef.current === attempt) {
          apply({ type: "probe.save.finished", ok: outcome.status === "saved" });
        } else if (outcome.status === "failed") {
          // A restart has already replaced the stage this call ended on.
          toastManager.add({ type: "error", title: "The previous interview couldn't be saved." });
        }
        if (outcome.status === "saved" && outcome.interview !== null) {
          onSavedRef.current?.(organization, outcome.interview);
        }
      });
    },
    [apply],
  );

  const teardown = useCallback(() => {
    attemptRef.current += 1;
    persist(false);
    const current = resources.current;
    resources.current = null;
    sendRef.current = null;
    pendingAsk.current = null;
    if (current === null) return;
    if (current.closeTimer !== null) clearTimeout(current.closeTimer);
    if (current.openingPoll !== null) clearInterval(current.openingPoll);
    if (current.connectTimer !== null) clearTimeout(current.connectTimer);
    cancelAnimationFrame(current.raf);
    current.channel.close();
    current.peer.close();
    for (const track of current.stream.getTracks()) track.stop();
    current.audio.pause();
    current.audio.srcObject = null;
    void current.context.close().catch(() => undefined);
    activityRef.current = 0;
    setActivity("listening");
    setMuted(false);
  }, [persist]);

  const hangUp = useCallback(
    (reason?: string) => {
      if (resources.current === null) return;
      teardown();
      apply(reason === undefined ? { type: "probe.ended" } : { type: "probe.ended", reason });
    },
    [apply, teardown],
  );

  const fail = useCallback(
    (message: string) => {
      teardown();
      apply({ type: "probe.failed", message });
    },
    [apply, teardown],
  );

  useEffect(() => teardown, [teardown]);

  useEffect(() => {
    const onPageHide = () => persist(true);
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [persist]);

  /** Put a typed answer on the wire and in the transcript. */
  const deliverAsk = useCallback(
    (typed: string) => {
      const send = sendRef.current;
      if (send === null) return;
      // The transcript shows what the examiner actually received.
      const text = typed.slice(0, MAX_TYPED_CHARS);
      apply({ type: "probe.typed", text });
      send({
        type: "session.instructions.append",
        delegation_id: null,
        content: typedAnswerInstructions(text),
      });
    },
    [apply],
  );

  const start = useCallback(async () => {
    const organization = orgRef.current;
    if (organization === null || resources.current !== null) return;
    if (
      typeof RTCPeerConnection === "undefined" ||
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      apply({ type: "probe.failed", message: describeStartFailure("unsupported") });
      return;
    }
    const attempt = ++attemptRef.current;
    const isCurrent = () => attemptRef.current === attempt;
    apply({ type: "probe.connecting" });
    savedSignature.current = null;

    let stream: MediaStream;
    // A browser asking for the microphone waits on the person; say so rather
    // than leaving "Connecting…" up while the prompt sits unanswered.
    const promptNotice = setTimeout(() => {
      if (isCurrent()) apply({ type: "probe.awaiting-microphone" });
    }, MICROPHONE_PROMPT_NOTICE_MS);
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch {
      if (isCurrent()) apply({ type: "probe.failed", message: describeStartFailure("microphone") });
      return;
    } finally {
      clearTimeout(promptNotice);
    }
    if (!isCurrent()) {
      // Restarted, ended or left while the prompt was up: release the
      // microphone this attempt was granted and build nothing.
      for (const track of stream.getTracks()) track.stop();
      return;
    }
    apply({ type: "probe.microphone-granted" });

    const peer = new RTCPeerConnection();
    // Created before the offer, so the offer negotiates it.
    const channel = peer.createDataChannel("oai-events");
    const audio = new Audio();
    audio.autoplay = true;
    const context = new AudioContext();
    const call: CallResources = {
      peer,
      channel,
      stream,
      audio,
      context,
      raf: 0,
      closeTimer: null,
      openingPoll: null,
      connectTimer: null,
    };
    resources.current = call;

    let remoteAnalyser: AnalyserNode | null = null;
    const buffer = new Uint8Array(new ArrayBuffer(1024)) as Uint8Array<ArrayBuffer>;
    // The microphone is read only to hold the examiner's opening while the
    // person is already talking — never drawn: the orb is the examiner.
    const localAnalyser = context.createAnalyser();
    localAnalyser.fftSize = 1024;
    context.createMediaStreamSource(stream).connect(localAnalyser);
    let lastLocalSpeech = Number.NEGATIVE_INFINITY;
    let localSpeechMs = 0;
    let lastTick = performance.now();
    let speaking = false;
    let quietSince = 0;
    const tick = (now: number) => {
      if (readLevel(localAnalyser, buffer) > LOCAL_SPEECH_LEVEL) {
        lastLocalSpeech = now;
        if (sendRef.current !== null) localSpeechMs += now - lastTick;
      }
      lastTick = now;
      const level = remoteAnalyser === null ? 0 : readLevel(remoteAnalyser, buffer);
      activityRef.current = level;
      if (!speaking && level > SPEAKING_ON) {
        speaking = true;
        quietSince = now;
        setActivity("speaking");
      } else if (speaking) {
        if (level > SPEAKING_OFF) quietSince = now;
        else if (now - quietSince > SPEAKING_HOLD_MS) {
          speaking = false;
          setActivity("listening");
        }
      }
      call.raf = requestAnimationFrame(tick);
    };
    call.raf = requestAnimationFrame(tick);

    peer.ontrack = (event) => {
      const remote = event.streams[0] ?? new MediaStream([event.track]);
      audio.srcObject = remote;
      void audio.play().catch(() => undefined);
      remoteAnalyser = context.createAnalyser();
      remoteAnalyser.fftSize = 1024;
      context.createMediaStreamSource(remote).connect(remoteAnalyser);
    };
    peer.onconnectionstatechange = () => {
      if (resources.current !== call) return;
      if (peer.connectionState === "failed") fail("The connection dropped.");
      if (peer.connectionState === "closed") hangUp();
    };
    for (const track of stream.getAudioTracks()) peer.addTrack(track, stream);

    const send = (event: Record<string, unknown>) => {
      if (channel.readyState === "open") channel.send(JSON.stringify(event));
    };
    let jurisdictions: ReadonlyArray<ProbeJurisdiction> = [];
    let sessionId: string | null = null;
    let personTranscribed = false;

    /**
     * Tool outputs owed per delegation. Each call starts the moment the
     * backend finishes writing it; the outputs go back together when that
     * backend response settles, followed by one `response.create`.
     */
    const owed = new Map<string, Array<Promise<{ callId: string; output: string }>>>();
    const keyOf = (delegationId: string | null) => delegationId ?? "";

    channel.onmessage = (message) => {
      if (resources.current !== call) return;
      const decoded = decodeLiveEvent(String(message.data));
      if (Option.isNone(decoded)) return;
      const event = decoded.value;
      apply(event);

      if (event.type === "session.started") {
        if (call.connectTimer !== null) clearTimeout(call.connectTimer);
        call.connectTimer = null;
        sendRef.current = send;
        const queued = pendingAsk.current;
        pendingAsk.current = null;
        if (queued !== null) {
          deliverAsk(queued);
          return;
        }
        const startedAt = performance.now();
        call.openingPoll = setInterval(() => {
          const now = performance.now();
          const decision = decideOpening({
            sinceStartMs: now - startedAt,
            sinceLocalSpeechMs: now - lastLocalSpeech,
            localSpeechMs,
            personTranscribed,
          });
          if (decision === "wait" || call.openingPoll === null) return;
          clearInterval(call.openingPoll);
          call.openingPoll = null;
          // Steered: the person spoke first, so the examiner answers them
          // rather than reciting its opening over them.
          if (decision === "open") {
            send({
              type: "session.instructions.append",
              delegation_id: null,
              content: openingInstructions(jurisdictions.map((item) => item.name)),
            });
          }
        }, 150);
        return;
      }
      if (event.type === "session.input_transcript.delta") {
        personTranscribed = true;
        return;
      }
      if (event.type === "session.closed") {
        hangUp(event.reason);
        return;
      }

      const fnCall = functionCallOf(event);
      if (Option.isSome(fnCall)) {
        const { callId, name } = fnCall.value;
        const args = parseToolArguments(fnCall.value.arguments);
        apply({ type: "probe.tool.started", callId, name, arguments: args });
        const pending = runTool(organization, sessionId, fnCall.value, args).then(
          ({ ok, reason, output, sources, records }) => {
            if (resources.current === call) {
              apply({
                type: "probe.tool.finished",
                callId,
                ok,
                ...(reason === undefined ? {} : { reason }),
                sources,
                records,
              });
            }
            return { callId, output };
          },
        );
        const key = keyOf(fnCall.value.delegationId);
        owed.set(key, [...(owed.get(key) ?? []), pending]);
        return;
      }

      const settled = settledDelegationOf(event);
      if (Option.isSome(settled)) {
        const key = keyOf(settled.value);
        const outputs = owed.get(key);
        if (outputs === undefined || outputs.length === 0) return;
        owed.delete(key);
        void Promise.all(outputs).then((results) => {
          if (resources.current !== call) return;
          for (const { callId, output } of results) {
            send({
              type: "response.item.create",
              item: { type: "function_call_output", call_id: callId, output },
            });
          }
          send({ type: "response.create" });
        });
      }
    };

    try {
      await peer.setLocalDescription(await peer.createOffer());
      await iceSettled(peer);
      // Ended or left during the ICE wait: starting a session now would
      // only leave an unused one running on OpenAI's side.
      if (resources.current !== call) return;
      const sdp = peer.localDescription?.sdp;
      if (!sdp) throw new Error("No local SDP offer");
      const grant = await openSession(organization, sdp, voiceRef.current, modeRef.current);
      if (resources.current !== call) return;
      if ("failure" in grant) {
        fail(grant.failure);
        return;
      }
      jurisdictions = grant.jurisdictions;
      sessionId = grant.sessionId;
      setModel(grant.model);
      await peer.setRemoteDescription({ type: "answer", sdp: grant.sdp });
      // The session exists; if the voice connection never comes up (a
      // network that blocks WebRTC), end with that reason, not a spinner.
      call.connectTimer = setTimeout(() => {
        call.connectTimer = null;
        if (resources.current === call && sendRef.current === null) {
          fail(describeStartFailure("blocked"));
        }
      }, CONNECT_TIMEOUT_MS);
    } catch {
      if (resources.current === call) fail(describeStartFailure("network"));
    }
  }, [apply, deliverAsk, fail, hangUp]);

  const end = useCallback(() => {
    const current = resources.current;
    if (current === null) return;
    const send = sendRef.current;
    if (send === null || current.closeTimer !== null) {
      hangUp();
      return;
    }
    // Ask GPT-Live to close and wait for `session.closed` (which hangs up),
    // but never longer than the grace period.
    send({ type: "session.close" });
    current.closeTimer = setTimeout(() => hangUp(), CLOSE_GRACE_MS);
  }, [hangUp]);

  const ask = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      if (sendRef.current !== null) {
        deliverAsk(trimmed);
        return;
      }
      pendingAsk.current = trimmed;
      if (resources.current === null) void start();
    },
    [deliverAsk, start],
  );

  const restart = useCallback(() => {
    teardown();
    void start();
  }, [start, teardown]);

  const toggleMute = useCallback(() => {
    const current = resources.current;
    if (current === null) return;
    const next = !muted;
    // The track stops the audio leaving the room; the event tells GPT-Live
    // the silence is deliberate rather than a pause to fill.
    for (const track of current.stream.getAudioTracks()) track.enabled = !next;
    sendRef.current?.({ type: next ? "session.input_audio.mute" : "session.input_audio.unmute" });
    setMuted(next);
  }, [muted]);

  return {
    state,
    active: state.phase === "live",
    muted,
    model,
    activity,
    activityRef,
    start,
    end,
    toggleMute,
    ask,
    restart,
  };
}
